import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router';
import { useSession } from '@zagru/platform';
import { CallbackPage } from './auth/CallbackPage';
import { LoginPage } from './auth/LoginPage';
import { LogoutPage } from './auth/LogoutPage';
import { RequireAuth } from './auth/RequireAuth';
import { landingPage } from './navigation/navConfig';
import { ShellSkeleton } from './shell/ShellSkeleton';
import { useShellT } from './shell/i18n';
import { RouteErrorBoundary } from '@/features/shared/RouteErrorBoundary';
import { portalRoutes } from '@/portal/PortalRoutes';
import { carrierRoutes } from '@/carrier/CarrierRoutes';

/*
 * The route table's frame, kept small because it is in the entry chunk (Wave 4 rule 11: entry ≤ 280 KB gz): the sign-in
 * and public pages, the portals, and the signed-in shell with only its landing redirect. Every other screen of the
 * shell — the feature modules' route groups, which reference hundreds of lazy screens — is in `routes.tsx`, its own
 * chunk: the browser router patches those routes in on the first navigation that needs them (React Router's
 * `patchRoutesOnNavigation`), and `bootstrap()` starts fetching them at start-up, next to the language catalogues.
 */

/** The shell's pathless content route; the screens are patched in as its children. */
export const SHELL_CONTENT_ROUTE_ID = 'shell-content';

/** Redirects `/` to the landing page of the current role profile. */
function LandingRedirect() {
  const { uiProfile } = useSession();
  return <Navigate to={landingPage[uiProfile]} replace />;
}

/**
 * What a full page load shows while the first route's lazy module arrives. Without it React Router rendered nothing
 * (a white page, then the shell popping in — "No `HydrateFallback` element provided") on every reload and deep link;
 * the shell skeleton is also what RequireAuth shows while the session is restored, so the two steps read as one.
 */
function BootFallback() {
  const t = useShellT();
  return <ShellSkeleton label={t('shell.loading')} />;
}

/**
 * Full route tree: public auth routes + the protected shell, with the shell's screens (`shellChildren`) passed in —
 * `routes.tsx` passes all of them (tests, `createTestRouter`); the browser router starts with none and patches them in.
 */
export function buildAppRoutes(shellChildren: RouteObject[]): RouteObject[] {
  return [
    { path: '/login', Component: LoginPage },
    { path: '/auth/callback', Component: CallbackPage },
    { path: '/logout', Component: LogoutPage },
    // Public quote flow (CPQ): renders its own `PublicShell`, outside the authenticated app shell.
    // Outside `AppLayout`, so the shell's module registration never runs here: CPQ registers its statuses itself.
    {
      path: 'quote/:tenantCode/*',
      lazy: () =>
        Promise.all([import('@/features/cpq/public/PublicQuotePage'), import('@/features/cpq/register')]).then(([m, r]) => {
          r.registerCpq();
          return { Component: m.PublicQuotePage };
        }),
    },
    // P-32: a shipment's public tracking page (signed, expiring link) — public, outside the signed-in shell.
    { path: 'track/:token', lazy: () => import('@/features/tracking/PublicTrackingPage').then((m) => ({ Component: m.PublicTrackingPage })) },
    // L-09 (D-322): a consignee books the delivery window of one leg (signed, expiring link) — public, outside the shell.
    { path: 'book/:token', lazy: () => import('@/features/tracking/PublicBookingPage').then((m) => ({ Component: m.PublicBookingPage })) },
    // P-13: an invitation e-mail's activation link — public, outside the signed-in shell.
    { path: 'invitation/:token', lazy: () => import('@/features/invitation/InvitationPage').then((m) => ({ Component: m.InvitationPage })) },
    // E-02: a campaign message's preference page (signed token, unsubscribe) — public, outside the signed-in shell.
    { path: 'preferences/:token', lazy: () => import('@/features/campaignPreferences/PublicPreferencesPage').then((m) => ({ Component: m.PublicPreferencesPage })) },
    // The customer portal is a second shell (ADR-0039 §6): `RequireAuth` + `RequirePermission` of its own, outside `AppLayout`.
    ...portalRoutes,
    // P-32 (D-132): the carrier portal, a third shell of the same kind.
    ...carrierRoutes,
    // P-33 (D-135): a driver signs in on the web only to be told to use the phone app.
    { path: 'driver-app', lazy: () => import('@/features/driverApp/DriverAppLandingPage').then((m) => ({ element: <RequireAuth><m.DriverAppLandingPage /></RequireAuth> })) },
    {
      path: '/',
      /*
       * The signed-in shell is its own chunk (Wave 4 rule 11: entry ≤ 280 KB gz): the router loads it alongside the first
       * screen's chunk — both lazy, fetched in parallel — so the login page and the public pages never carry it, and a
       * deep link waits on no extra round trip. `BootFallback` covers the wait, as it did for the screen alone.
       */
      lazy: () =>
        import('./shell/AppLayout').then((m) => ({
          element: (
            <RequireAuth>
              <m.AppLayout />
            </RequireAuth>
          ),
        })),
      errorElement: <RouteErrorBoundary />,
      HydrateFallback: BootFallback,
      children: [
        {
          // Pathless layout so route errors render inside the shell.
          id: SHELL_CONTENT_ROUTE_ID,
          element: <Outlet />,
          errorElement: <RouteErrorBoundary />,
          children: [
            { index: true, Component: LandingRedirect },
            ...shellChildren,
          ],
        },
      ],
    },
  ];
}

/** What the router needs from `routes.tsx`. */
interface ShellRoutesModule {
  shellRoutes: RouteObject[];
}
let shellRoutesModule: Promise<ShellRoutesModule> | undefined;

/** Starts (once) fetching the shell's screens — `main.tsx` calls it at start-up, so the first navigation rarely waits. */
export function preloadShellRoutes(): Promise<ShellRoutesModule> {
  shellRoutesModule ??= import('./routes').catch((error: unknown) => {
    shellRoutesModule = undefined; // a failed fetch (a deploy, a dropped connection) is retried on the next navigation
    throw error;
  });
  return shellRoutesModule;
}

/**
 * The browser router: the frame above, and the shell's screens patched in on the first navigation that does not match
 * the frame (any signed-in screen but `/`). Patched once; React Router calls this again on every navigation to a
 * dynamic path (it may reveal a more specific route), which then returns at once.
 */
export function createAppRouter() {
  let patched = false;
  return createBrowserRouter(buildAppRoutes([]), {
    async patchRoutesOnNavigation({ patch, signal }) {
      if (patched) return;
      const { shellRoutes } = await preloadShellRoutes();
      if (patched || signal.aborted) return;
      patch(SHELL_CONTENT_ROUTE_ID, shellRoutes);
      patched = true;
    },
  });
}
